import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";
import { NumericValue } from "@smithy/core/serde";
import Decimal from "decimal.js";
import { getCurrencyData } from "country-currency-utils";

/**
 * Currency decimal places configuration, similar to FuseBillingService CurrencyConfig:
 * https://code.amazon.com/packages/FuseBillingService/blobs/mainline/--/src/com/amazon/fusebillingservice/util/CurrencyConfig.java
 *
 * 3-tier resolution for decimal places:
 * 1. Override list of unscaled currency codes (no rounding applied) — matching FBS fusen.unscaled.currencycode config
 * 2. ISO 4217 lookup via country-currency-utils (getCurrencyData.decimals)
 * 3. Default to 2 decimal places for unknown codes
 */

// Tier 1: Unscaled currency codes (matching FBS fusen.unscaled.currencycode config).
// These currencies are not scaled — amounts are returned as-is without rounding.
const UNSCALED_CURRENCY_CODES = new Set<string>(["KWD"]);

// Tier 3: Default fraction digits for unknown currency codes
const DEFAULT_FRACTION_DIGITS = 2;

/**
 * Returns the number of decimal places to use for a given currency code,
 * or null if the currency is unscaled (no rounding should be applied).
 */
const getDecimalPlaces = (currencyCode: string): number | null => {
  // Tier 1: unscaled override
  if (UNSCALED_CURRENCY_CODES.has(currencyCode)) {
    return null;
  }
  // Tier 2: ISO 4217 lookup
  const currencyData = getCurrencyData(currencyCode);
  if (currencyData) {
    return currencyData.decimals;
  }
  // Tier 3: default
  return DEFAULT_FRACTION_DIGITS;
};

/**
 * Determines whether a FixedFee config has mismatched contract and billing
 * currency codes AND a stored FX rate, making it eligible for FX conversion.
 */
export const isFxApplicable = (fixedFeeConfig: FixedFeeConfig): boolean => {
  return (
    !!fixedFeeConfig.contractCurrencyCode &&
    !!fixedFeeConfig.billingCurrencyCode &&
    fixedFeeConfig.contractCurrencyCode !==
      fixedFeeConfig.billingCurrencyCode &&
    !!fixedFeeConfig.fxRate
  );
};

/**
 * Applies the stored FX rate to convert a contract-currency amount to billing currency,
 * rounded to the billing currency's decimal places per the 3-tier config above.
 */
const applyFx = (
  amount: NumericValue | Decimal | number | string | undefined | null,
  fxRate: NumericValue | undefined,
  billingCurrencyCode: string,
): string | null => {
  if (amount == null || !fxRate) return null;

  let decimalAmount: Decimal;
  if (amount instanceof NumericValue) {
    decimalAmount = new Decimal(amount.string);
  } else if (amount instanceof Decimal) {
    decimalAmount = amount;
  } else {
    decimalAmount = new Decimal(String(amount));
  }

  const rate = new Decimal(fxRate.string);
  const converted = decimalAmount.mul(rate);

  const decimalPlaces = getDecimalPlaces(billingCurrencyCode);
  if (decimalPlaces == null) {
    // unscaled — return full precision
    return converted.toString();
  }
  return converted.toFixed(decimalPlaces);
};

/**
 * Formats a contract amount with its FX-converted billing equivalent.
 * Returns the locale-formatted converted value string, or null if conversion is not possible.
 */
export const formatWithFx = (
  amount: NumericValue | Decimal | number | string | undefined | null,
  fxRate: NumericValue | undefined,
  billingCurrencyCode?: string,
): string | null => {
  if (!billingCurrencyCode) return null;
  const converted = applyFx(amount, fxRate, billingCurrencyCode);
  if (converted == null) return null;

  const decimalPlaces = getDecimalPlaces(billingCurrencyCode);
  return Number(converted).toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces ?? undefined,
    maximumFractionDigits: decimalPlaces ?? 10,
  });
};
